import i18n from "../i18n";
import { bustProgramEditorCache } from "../utils/bust-loader-cache";

/*
 * Action handlers for workout day sections (text separators). They proxy to the
 * `exercise-section` API resource, carrying proxyAthlete/reqSource like the
 * exercise actions. Sections are an additive overlay — deleting one never
 * removes exercises.
 */
const ExerciseSection = (app) => {
  app
    // Create a section for a calendar day or a program session.
    .post("/exercise-section/create", async ({ req, res, api, session }) => {
      const { body, query = {} } = req;
      const { proxyAthlete, reqSource, organizationCoach } = query;

      // Program-scoped sections arrive with flat cycle fields; rebuild the
      // nested cycle object the API expects (calendar sections use utcDate).
      if (body.cycleId) {
        body.cycle = {
          id: body.cycleId,
          program: Number(body.program),
          programWeek: Number(body.programWeek),
          programSession: Number(body.programSession),
          isScheduled: false,
        };
        delete body.cycleId;
        delete body.program;
        delete body.programWeek;
        delete body.programSession;
      }

      let sectionQuery = `?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;
      if (organizationCoach) {
        sectionQuery += `&organizationCoach=${organizationCoach}`;
      }

      const created = await api(`exercise-section${sectionQuery}`, {
        method: "POST",
        body,
      });
      const doc = await created.json();

      bustProgramEditorCache(session);
      // Carry the new section's id back through the query string so the
      // client can drop it straight into edit mode, focused and scrolled
      // into view, instead of requiring a double-click to start renaming it.
      res.refreshWith({ editSection: doc._id }, { url: "back", hash: "#_" });
    })
    // Update a section (rename and/or reposition via `order`).
    .post(
      "/exercise-section/update/:id",
      async ({ req, res, api, session }) => {
        const { params, body, query = {} } = req;
        const { proxyAthlete, reqSource, organizationCoach } = query;

        // Moving a program section to another session: update only those two
        // cycle paths so id/program/isScheduled stay untouched.
        if (body.programWeek != null) {
          body["cycle.programWeek"] = Number(body.programWeek);
          body["cycle.programSession"] = Number(body.programSession);
          delete body.programWeek;
          delete body.programSession;
        }

        let sectionQuery = `?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;
        if (organizationCoach) {
          sectionQuery += `&organizationCoach=${organizationCoach}`;
        }

        await api(`exercise-section/${params.id}${sectionQuery}`, {
          method: "PUT",
          body,
        });

        bustProgramEditorCache(session);
        // Clear any pending "just created" auto-edit marker now that the name
        // has been saved.
        res.refreshWith({ editSection: "" }, { url: "back", hash: "#_" });
      }
    )
    // Delete a section or a workout note. Exercises are never removed.
    .post(
      "/exercise-section/remove/:id",
      async ({ req, res, flash, api, session }) => {
        const { params, query = {} } = req;
        const { proxyAthlete, reqSource, organizationCoach, kind } = query;

        let sectionQuery = `?proxyAthlete=${proxyAthlete}&reqSource=${reqSource}`;
        if (organizationCoach) {
          sectionQuery += `&organizationCoach=${organizationCoach}`;
        }

        await api(`exercise-section/${params.id}${sectionQuery}`, {
          method: "DELETE",
        });

        flash(
          "success",
          kind === "workout-note"
            ? i18n.t("athlete-portal:exerciseEditor.workoutNoteDeleted")
            : i18n.t("athlete-portal:exerciseEditor.sectionDeleted")
        );
        bustProgramEditorCache(session);
        res.refreshWith({ editSection: "" }, { url: "back", hash: "#_" });
      }
    );
};

export default ExerciseSection;
